/**
 * This is a class that preloads background images for a better user experience in Skye Learning 
 * based on HTML class designations.
 * 
 * Instead of preloading all the bg images in the whole site, this class will preload bg images
 * on a per-page basis, and only for elements that have matching classes that are specified
 * in an array (inputDataObject). This way we are not preloading all images in the entire
 * site for a user that only looks at one or two pages on a mobile device, for example.
 *
 * Note - the original version was integrated in this project in 2018,
 * and it was written in a JS prototype object pattern. This is a modernized update.
 *
 */

class BannerImagePreloader {
    /**
     * @param {HTML Object} pageContent provides access to the pageContent element
     * 
     */
    constructor(pageContent) {
        this.inputDataObject = [];
        this.classTarget = "";
        this.errorBuffer = false;
        this.pageContent = pageContent;
    }

    /**
     * @method setImageArray - This method sets the array width data that provides the image names for preloading and other data pertaining to themes.
     * 
     * @return array - returns an array containing image names for prelading and theme data
     */
    setImageArray = (inputDataObject) => {
        this.inputDataObject = inputDataObject;
        return inputDataObject;
    }

    /**
     * @method getActiveTheme - This method checks for an active theme and then returns the data for it
     * 
     * @return object - returns the object that contains the active theme's specifics
     */
    getActiveTheme = () => {
        let bodyElement = document.querySelector('body');
        let activeThemeObject = {};
        let bodyElemClassName = bodyElement.className;
        for (var count = 0; count < this.inputDataObject.theme.data.length; count += 1) {
            if (bodyElemClassName.match(this.inputDataObject.theme.data[count].class)) {
                activeThemeObject = this.inputDataObject.theme.data[count];
            }
        }
        return activeThemeObject;
    }
    /**
     * @method getActiveThemeNameForRegex
     * This method strips away the theme--skye- from a theme class name so that the remainder can
     * be used to detect a match in image names that belong to specific themes.
     * 
     * @return string - the result is a string that is the most basic name of a theme 
     */
    getActiveThemeNameForRegex = () => {
        let themeName = this.getActiveTheme().class;
        let themeNameForRegex = themeName.split('theme--skye-');
        return themeNameForRegex[1];
    }
    /**
     * @method getActiveThemePattern
     * This method provivdes a string for differentiating between different sets of theme images for the same page.
     * @return string - returns a string.
     */
    getActiveThemePattern = () => {
        let activeTheme = this.getActiveThemeNameForRegex();
        let themeNameTag = activeTheme.split('-');
        let themePattern = themeNameTag.join().replace(',', '');
        return themePattern;
    }
    /**
     * @method selectKey - Method for returning the #pageContent element HTML class that will
     * be used to select the correct images list from the imageArray.
     *
     * @return string - returns the classKey which matches the current page - extracted and compared against the #pageContent classList.
     */
    selectKey = () => {
        let pageContentClass = pageContent.className;
        let objectKeys = Object.keys(this.inputDataObject.pages);
        let classKey;
        for (var icounter = 0; icounter < objectKeys.length; icounter += 1) {
            if (pageContentClass.match(objectKeys[icounter])) {
                classKey = objectKeys[icounter];
                break;
            }
        }

        this.classTarget = classKey;
        
        try {
            if (this.inputDataObject[classKey].length === 0)
                throw "The object image array object for the key" +
                " " +
                classKey +
                " is empty.";
        } catch (err) {
            //console.warn('Warning! The image Array[' + classKey + '] is empty. No banner images to preload for this page!');
            this.errorBuffer = true;
        } finally {
            return classKey;
        }
    };

    /**
     * @method runPreloadingLoop - Method that loops through the selected imageArray list
     * and creates preload objects based on the src attributes of the HTML elements which
     * have the specified classes from the selected list.
     */
    runPreloadingLoop = () => {
        if (this.classTarget === null || this.classTarget === '' || this.classTarget === undefined) {
            //console.warn("No images are selected for preloading in this page.");
            this.errorBuffer = true;
        } else {
            let scounter;
            let pages = this.inputDataObject.pages;
            let classKey = this.classTarget;
            let fArray = this.inputDataObject.pages[this.classTarget];
            var theRegularSrc = "";
            var theThemeImgSrc = "";
            let activeThemePattern = this.getActiveThemePattern();
            let themeRegexPattern = this.getActiveThemeNameForRegex();
            let activeThemeObject = this.getActiveTheme();

            let keysFromObject = Object.keys(fArray);
            let selectedThemeObjectKey;

            if (fArray.length === null || fArray.length === undefined) { // condition for pages that have more than one set of banner images for  different themes
                for (scounter = 0; scounter < keysFromObject.length; scounter += 1) {
                    if (keysFromObject[scounter].match(activeThemePattern)) {
                        selectedThemeObjectKey = keysFromObject[scounter];
                        break;
                    }
                }

                var themeObjectSelection = pages[classKey][selectedThemeObjectKey];
                for (var subobjcnt = 0; subobjcnt < themeObjectSelection.length; subobjcnt += 1) {
                    theThemeImgSrc = activeThemeObject.imgPrefix + themeObjectSelection[subobjcnt];
                    this.buildImageObject(theThemeImgSrc, subobjcnt);
                }
                return themeObjectSelection;
            }

            if (fArray.length) {
                for (var lcounter = 0; lcounter < fArray.length; lcounter += 1) {
                    if (fArray[lcounter].match(themeRegexPattern)) {
                        theRegularSrc = activeThemeObject.imgPrefix + fArray[lcounter];
                    } else {
                        theRegularSrc = 'https://cdn.skyelearning.com/static/' + fArray[lcounter];
                    }
                    this.buildImageObject(theRegularSrc, lcounter);
                }
                return fArray;
            }

        }
    };
    /**
     * @method - this method is used for building image objects that will be appended to the body element as preloaded images.
     */
    buildImageObject = (imageSrc, index) => {
        let theImageObject = new Image(0, 0);
        theImageObject.src = imageSrc;
        theImageObject.style.position = "absolute";
        theImageObject.style.display = "none";
        theImageObject.alt = " PreLoaded Banner Img" + index;
        document.body.appendChild(theImageObject);
    }
}
export default BannerImagePreloader;